import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import { Alert, Avatar, Button, CircularProgress, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState } from 'react'

import { Box } from '@/components/ui'
import { isOrgFeatureOn } from '@/definitions/featureFlags'
import { useConfirm } from '@/hooks/useConfirm'
import { useOrgFeatureFlags } from '@/hooks/useOrgFeatureFlags'
import { useSetOrgFeatureFlags } from '@/hooks/useSetOrgFeatureFlags'
import { useSnackbar } from '@/hooks/useSnackbar'
import { PageCondensed } from '@/layouts'
import { borderWidth, spacing } from '@/themes/tokens'
import { getApiErrorMessage } from '@/utils/apiErrors'

import { useRouteOrg } from '../../orgRoute'
import { OrgFeatureFlagRow } from './components/OrgFeatureFlagRow'
import { buildFlagChanges, hasFlagChanges, unmovedFlags } from './utils/buildFlagChanges'

const SAVE_FAILED_MESSAGE = 'These feature flag changes could not be saved.'

const SAVE_SUCCEEDED_MESSAGE = 'Feature flags updated.'

const PARTIAL_SUCCESS_MESSAGE =
  'Saved, but some flags did not move. Their chips say why.'

const INTRO = 'Turn a feature flag on or off for this organization.'

/**
 * The one region that scrolls.
 *
 * `minHeight: 0` is what makes it the one: without it a flex item refuses to
 * shrink below its content, and a long list would push the save buttons off the
 * bottom instead of scrolling under them. Scoped to `md` and up, because that is
 * where `PageCondensed fullHeight` gives the column a definite height to shrink
 * within — below it the page scrolls whole, as the sibling Product Flags tab does.
 */
const FlagList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.sm),
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Keeps the scrollbar off the rows' rounded edge.
    paddingRight: theme.spacing(spacing.md),
  },
}))

const SaveActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(spacing.md),
  borderTop: `${borderWidth.thin}px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(spacing.lg),
}))

/**
 * Which feature flags reach this practice.
 *
 * The switches stage rather than write as they are clicked, so several flags move
 * in one request. Only genuine differences are sent — a flag nobody touched is
 * absent from the request, which keeps a stale page from rewriting something
 * somebody else just changed.
 */
export const FeatureFlagsTab = () => {
  const org = useRouteOrg()
  const confirm = useConfirm()
  const { showSuccess, showWarning } = useSnackbar()
  const { data: flags, isLoading, isError } = useOrgFeatureFlags(org.id)
  const { mutateAsync, isPending, error } = useSetOrgFeatureFlags()

  // Only the switches the operator has moved; everything else reads from `flags`.
  const [staged, setStaged] = useState<Record<string, boolean>>({})

  const saveError = error === null ? null : getApiErrorMessage(error, SAVE_FAILED_MESSAGE)
  const changes = buildFlagChanges(flags ?? [], staged)
  const isDirty = hasFlagChanges(changes)

  const handleStage = (name: string, isStaged: boolean) => {
    setStaged((current) => ({ ...current, [name]: isStaged }))
  }

  const handleSave = async () => {
    // Only worth a dialog when the save takes something away; granting is undone
    // by the same switch.
    if (changes.disable.length > 0) {
      const confirmed = await confirm({
        title: `Turn off ${changes.disable.length} feature flags?`,
        body: (
          <Typography variant='body2'>
            {changes.disable.join(', ')} will stop reaching this practice immediately.
          </Typography>
        ),
        confirmLabel: 'Save changes',
        destructive: true,
      })
      if (!confirmed) {
        return
      }
    }

    const stored = await mutateAsync({ orgId: org.id, data: changes })
    setStaged({})

    // A save can land while a named flag does not move — a window somebody set
    // stands in the way — so the report follows the states read back after the
    // write, never the ones that were submitted.
    if (unmovedFlags(stored, changes).length > 0) {
      showWarning(PARTIAL_SUCCESS_MESSAGE)
    } else {
      showSuccess(SAVE_SUCCEEDED_MESSAGE)
    }
  }

  return (
    <PageCondensed fullHeight>
      <Box variant='flexColumn' gap='lg' height='100%'>
        <Box variant='flex' gap='md'>
          <Avatar variant='titleIcon'>
            <FlagOutlinedIcon />
          </Avatar>
          <Box variant='flexColumn' gap='none' fullWidth={false}>
            <Typography variant='h4' fontWeight={600}>
              Feature Flags
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {INTRO}
            </Typography>
          </Box>
        </Box>

        {saveError !== null && <Alert severity='error'>{saveError}</Alert>}

        {isLoading && (
          <Box variant='flexCenter' py={8}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Typography color='text.secondary'>
            Feature flags could not be loaded.
          </Typography>
        )}

        {flags !== undefined && (
          <>
            <FlagList>
              {flags.map((flag) => (
                <OrgFeatureFlagRow
                  key={flag.name}
                  name={flag.name}
                  description={flag.description}
                  state={flag.state}
                  // Untouched rows follow what is stored; only a moved switch departs from it.
                  staged={staged[flag.name] ?? isOrgFeatureOn(flag.state)}
                  onStage={(isStaged) => handleStage(flag.name, isStaged)}
                />
              ))}
            </FlagList>

            <SaveActions>
              <Button
                variant='contained'
                disabled={!isDirty || isPending}
                onClick={handleSave}
              >
                {isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant='outlined'
                disabled={!isDirty || isPending}
                onClick={() => setStaged({})}
              >
                Reset to saved
              </Button>
            </SaveActions>
          </>
        )}
      </Box>
    </PageCondensed>
  )
}
